// helix-exception: dense badge text drawn at 8-11px in Figma has no Helix type-scale step (F-12)
/**
 * FamilyMemberDisplayCard — shared chrome primitive for family-member display cards
 * (Phase F.1c-iii Option B / GEN-2998).
 *
 * Owns the visual surface (outer card shell, header with optional Edit pencil,
 * optional badge row, optional bold name line, 4-column field grid, optional
 * `customBody` override, empty-state, and bottom `StatusPill`) that today's
 * `<ComparatorCard>` and `<GuardianContactCard>` both render verbatim.
 *
 * The primitive is intentionally domain-agnostic: callers compose their own
 * badges (e.g., `<TwinBadge>` is comparator-specific) and supply their own
 * field list via `FieldSpec[]`. This keeps comparator-specific logic in
 * `<ComparatorCard>` and guardian-contact-specific logic in
 * `<GuardianContactCard>` while sharing the chrome.
 *
 * Also re-exports `PrimaryGuardianBadge`, the brand-accented "Primary
 * guardian" pill that was previously duplicated across both cards. The
 * `formatDobForDisplay` helper lives in `./familyMemberDisplayHelpers` so
 * this file only exports React components (Fast Refresh ergonomics).
 *
 * No behavioural changes vs. the duplicated implementations: class strings,
 * dark-mode classes, and DOM structure are byte-equivalent so visual
 * regressions are zero. Existing `ComparatorCard.test.tsx` is the parity
 * proof for the comparator side.
 */

import type { ElementType, ReactNode } from 'react'
import { CardEmptyState } from './CardEmptyState'
import { CLICKABLE_CARD_SURFACE_CLASS, editButtonHoverFillClass, withStopPropagation } from './cardSurface'
import { StatusPill } from './FormCard'
import { Pen, UserCircle } from 'flowbite-react-icons/outline'

export function PrimaryGuardianBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border-brand-subtle dark:border-brand-800 bg-background-brand-softer dark:bg-gray-800 px-2.5 py-1 text-xs font-medium text-text-fg-info dark:text-brand-300">
      <UserCircle className="h-3.5 w-3.5 shrink-0 text-text-fg-info dark:text-brand-300" aria-hidden />
      {children}
    </span>
  )
}

/** A single cell in the 4-column field grid. */
export interface FieldSpec {
  /** Leading icon (Flowbite outline). Typed as ElementType so Flowbite's
   *  ForwardRefExoticComponent icons are accepted without coercion. */
  icon: ElementType
  /** Optional inline gray label rendered before the value (e.g. "DOB ", "MRN "). */
  inlineLabel?: string
  /** Display value; if null/empty the cell is blank (slot stays for grid alignment). */
  value?: string | null
  /** When true, render an em-dash placeholder if value is empty (e.g. guardian Relationship). */
  alwaysShow?: boolean
  /** Optional title attribute for truncation tooltips (used for email/phone). */
  title?: string
}

export interface FamilyMemberDisplayCardProps {
  /** Section title (e.g. "Mother", "Guardian Contact"). */
  title: string
  /** Optional Edit pencil callback. When omitted, no button is rendered. */
  onEdit?: () => void
  /** Aria label for the Edit button (e.g. "Edit mother"). */
  editAriaLabel?: string
  /**
   * Read-only header link rendered on the right of the header (in place of the
   * Edit pencil). Used when the card mirrors data owned elsewhere — the link
   * label shows where to go (e.g. a relationship like "Father") and `onClick`
   * navigates there. No whole-card click and no Edit pencil are rendered.
   */
  headerLink?: { label: string; onClick: () => void; ariaLabel?: string }
  /** Apply attention styling (orange accent + filled brand button). */
  needsAttention?: boolean
  /** Composed badge row rendered between header and body. */
  badges?: ReactNode
  /** Bold first+last name line above the field grid. */
  nameLine?: string | null
  /** Up to ~4 visible fields rendered in a 2/4-column grid. */
  fields?: FieldSpec[]
  /** When provided, replaces the entire body (name + grid). */
  customBody?: ReactNode
  /** Override for the default "None entered" copy. */
  emptyContent?: ReactNode
  /** Bottom status pill config. */
  status?: { text: string; variant?: 'success' | 'info' | 'neutral'; successLead?: 'check' | 'user' }
}

function isFieldVisible(f: FieldSpec): boolean {
  if (f.alwaysShow) return true
  const v = (f.value ?? '').trim()
  return v.length > 0
}

function FieldCell({ field }: { field: FieldSpec }) {
  const Icon = field.icon
  const trimmed = field.value?.trim()
  const visible = isFieldVisible(field)
  if (!visible) {
    return (
      <span className="flex min-w-0 items-center gap-1 text-sm text-text-heading dark:text-white" />
    )
  }
  const display = trimmed && trimmed.length > 0 ? trimmed : '—'
  const fontClass = field.inlineLabel ? 'font-medium' : ''
  return (
    <span
      className={`flex min-w-0 items-center gap-1 text-sm text-text-heading dark:text-white ${fontClass}`.trim()}
    >
      <Icon className="h-4 w-4 shrink-0 text-text-body dark:text-gray-400" aria-hidden />
      <span className="min-w-0 truncate leading-5" title={field.title ?? undefined}>
        {field.inlineLabel ? (
          <>
            <span className="text-text-body-subtle dark:text-gray-500">{field.inlineLabel}</span>
            <span className="text-text-body dark:text-gray-400">{display}</span>
          </>
        ) : (
          display
        )}
      </span>
    </span>
  )
}

export function FamilyMemberDisplayCard({
  title,
  onEdit,
  editAriaLabel,
  headerLink,
  needsAttention = false,
  badges,
  nameLine,
  fields,
  customBody,
  emptyContent,
  status,
}: FamilyMemberDisplayCardProps) {
  const cardClasses = needsAttention
    ? 'order-card-attention !p-4 sm:!p-7'
    : 'order-card !p-4 sm:!p-7'
  const isClickable = Boolean(onEdit)

  const visibleFields = (fields ?? []).filter(isFieldVisible)
  const hasName = Boolean(nameLine && nameLine.trim())
  const hasBody = Boolean(customBody) || hasName || visibleFields.length > 0

  return (
    <div
      className={`${cardClasses} w-full min-w-0 shadow-sm${isClickable ? ` ${CLICKABLE_CARD_SURFACE_CLASS}` : ''}`}
      onClick={isClickable ? onEdit : undefined}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <h2 className="flex-1 text-2xl font-semibold leading-8 tracking-tight text-text-heading dark:text-white">
            {title}
          </h2>
          {headerLink && (
            <button
              type="button"
              onClick={headerLink.onClick}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-medium text-text-fg-info transition-colors hover:bg-background-brand-softer dark:hover:bg-gray-800"
              aria-label={headerLink.ariaLabel ?? headerLink.label}
            >
              {headerLink.label}
              <Pen className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={withStopPropagation(onEdit)}
              className={`inline-flex items-center justify-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium transition-colors sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-xs ${
                needsAttention
                  ? 'bg-brand-600 text-white hover:bg-brand-700'
                  : 'border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800'
              } ${editButtonHoverFillClass(needsAttention)}`}
              aria-label={editAriaLabel}
            >
              <Pen className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
              Edit
            </button>
          )}
        </div>

        {badges ? <div className="flex flex-wrap gap-2">{badges}</div> : null}

        {hasBody ? (
          <div className="flex flex-col gap-4">
            {customBody ? (
              customBody
            ) : (
              <>
                {hasName ? (
                  <p className="text-lg font-bold leading-7 text-text-body dark:text-gray-400">
                    {nameLine}
                  </p>
                ) : null}

                {fields && fields.length > 0 ? (
                  <div className="grid w-full grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                    {fields.map((f, idx) => (
                      <FieldCell key={idx} field={f} />
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : emptyContent != null ? (
          <span className="text-base font-normal leading-6 text-text-body-subtle dark:text-gray-500">{emptyContent}</span>
        ) : (
          <CardEmptyState />
        )}

        {status ? (
          <StatusPill
            text={status.text}
            variant={status.variant}
            successLead={status.successLead}
          />
        ) : null}
      </div>
    </div>
  )
}
